import { useEffect } from 'react';
import { ISerialErrors, IShipmentData, IproductItemData, IreferencesByCountry } from '../../../../../../types/orders';
import ReferenceCard from '../ReferenceCard/ReferenceCard';
import { IOrganizationConfig } from '../../../../../../types/product.configuration.types';
import { SWR_KEYS } from 'constants/constants';
import { IStockProductAvailabilityData, IStockProductAvailabilityRequest } from 'types/stockOrders';
import { getStockProductAvailability } from 'services/stockOrders';
import useSWRMutation from 'swr/mutation';
import useStockReservationState from 'state/useStockReservationState';
import OrderCountrySkeleton from './OrderCountrySkeleton';

import './OrderCountry.scss';

const OrderCountry = ({
  countryName,
  countryData,
  countryCode,
  setHasDataChanged,
  isLast,
  serialErrorsList,
  changeErrorList,
  organizationName,
  updateListOfProductsToUpdate,
  savedOrganizationConfigs,
  updateWithoutLoaders,
  organizationId,
  orderId,
  selectedTabId,
  orderStatus,
  shimpmentData
}: {
  countryName: string;
  countryCode: string;
  countryData: IreferencesByCountry;
  setHasDataChanged: React.Dispatch<React.SetStateAction<boolean>>;
  isLast?: boolean;
  serialErrorsList?: ISerialErrors[];
  changeErrorList?: (items: ISerialErrors[]) => void;
  organizationName?: string;
  updateListOfProductsToUpdate: (product: IproductItemData) => void;
  updateWithoutLoaders: () => Promise<void>;
  organizationId?: number;
  orderId: string;
  savedOrganizationConfigs: IOrganizationConfig[];
  selectedTabId: number;
  orderStatus?: string;
  shimpmentData?: IShipmentData[];
}) => {
  const { references, exchangeRate, flag, countryId } = countryData;

  const {
    refreshStockProductAvailabilityData,
    setStockProductAvailabilityData,
    setRefreshStockProductAvailabilityData
  } = useStockReservationState();
  const { trigger: stockProductAvailabilityTrigger, isMutating: isLoadingGetStockProductAvailability } = useSWRMutation(
    SWR_KEYS.getStockProductAvailability,
    async (__, { arg }: { arg: IStockProductAvailabilityRequest }) => getStockProductAvailability(arg, countryCode),
    {
      onSuccess: (data: IStockProductAvailabilityData[]) => {
        setStockProductAvailabilityData(data);
        setRefreshStockProductAvailabilityData(false);
      }
    }
  );

  const firstReferenceItems = references[0].items || [];
  const currency = firstReferenceItems[0]?.currency || 'Sin información moneda';

  useEffect(() => {
    if (selectedTabId === 2 || refreshStockProductAvailabilityData) {
      stockProductAvailabilityTrigger(references?.map(reference => reference.sku ?? ''));
    }
  }, [refreshStockProductAvailabilityData, references, selectedTabId, stockProductAvailabilityTrigger]);

  if (isLoadingGetStockProductAvailability) {
    return (
      <OrderCountrySkeleton
        countryName={countryName}
        flag={flag}
        exchangeRate={exchangeRate}
        currency={currency}
        isLast={isLast}
      />
    );
  }

  return (
    <div className="orderCountry">
      <div className="countryHeader">
        <div className="countryFlag" style={{ backgroundImage: `url(${flag || ''})` }} />
        <div className="countryName">{countryName}</div>
        <div className="countryExchangeRate">
          Tasa de cambio: {exchangeRate}
          {currency}
        </div>
      </div>
      <div className="referencesList">
        {references.map((reference, i) => (
          <ReferenceCard
            key={`${reference.referenceId}-${reference.sku}-${i}`}
            reference={reference}
            setHasDataChanged={setHasDataChanged}
            countryId={countryId}
            countryCode={countryCode}
            serialErrorsList={serialErrorsList}
            changeErrorList={changeErrorList}
            organizationName={organizationName}
            updateListOfProductsToUpdate={updateListOfProductsToUpdate}
            updateWithoutLoaders={updateWithoutLoaders}
            savedOrganizationConfigs={savedOrganizationConfigs}
            organizationId={organizationId}
            orderId={orderId}
            orderStatus={orderStatus}
            shimpmentData={shimpmentData}
          />
        ))}
      </div>
      {!isLast && <div className="grayLine" />}
    </div>
  );
};

export default OrderCountry;
